/*!
 * Copyright © 2020-2026. Verizon Connect Ireland Limited. All rights reserved.
 */

import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';

import { PendoModule } from '@fleetmatics/ui.insights';

import { environment } from '../environments/environment';
import { RevealHeaderComponent } from './account-activation/components/reveal-header/reveal-header.component';
import { SetYourPasswordModule } from './account-activation/containers/set-your-password/set-your-password.module';
import { SuccessfullyVerifiedModule } from './account-activation/containers/successfully-verified/successfully-verified.module';
import { VerifyYourAccountModule } from './account-activation/containers/verify-your-account/verify-your-account.module';
import { PendoService, SetPasswordService, UrlsService } from './account-activation/services';
import { UserEffects } from './account-activation/store/effects';
import { accountReducers } from './account-activation/store/reducers';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';
import { CACHE_NONCE } from './core/config/cache-nonce.token';
import { CoreModule } from './core/core.module';

@NgModule({
  declarations: [AppComponent, RevealHeaderComponent],
  imports: [
    BrowserModule.withServerTransition({ appId: 'account-activation' }),
    BrowserAnimationsModule,
    AppRoutingModule,
    CoreModule,
    PendoModule.forRoot(environment.config),
    HttpClientModule,
    SetYourPasswordModule,
    VerifyYourAccountModule,
    SuccessfullyVerifiedModule,
    StoreModule.forRoot(accountReducers, {
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true
      }
    }),
    EffectsModule.forFeature([UserEffects]),
    StoreDevtoolsModule.instrument({
      name: 'Account Activation DevTools',
      maxAge: 25,
      logOnly: environment.production,
      connectInZone: true
    }),
    MatIconModule
  ],
  providers: [
    SetPasswordService,
    UrlsService,
    PendoService,
    { provide: MAT_RIPPLE_GLOBAL_OPTIONS, useValue: { disabled: true } },
    { provide: CACHE_NONCE, useValue: environment.config.cacheNonce }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor(private readonly registry: MatIconRegistry, private readonly sanitizer: DomSanitizer) {
    const icons = new Map<string, string>([
      ['ds-success', 'check_circle-24px.svg'],
      ['ds-error', 'error-24px.svg'],
      ['ds-reveal-logo', 'vzc-reveal.svg']
    ]);

    icons.forEach((fileName, name) => {
      this.registry.addSvgIcon(name, this.sanitizer.bypassSecurityTrustResourceUrl(`assets/${fileName}`));
    });

    this.registry.addSvgIconSet(this.sanitizer.bypassSecurityTrustResourceUrl('assets/ds-sprite.svg'));
  }
}
